import { useRef, useState } from "react";
import {
  usePlaybackMediator,
  useVideoPlayback,
} from "@/contexts/playback-context";
import wretch from "wretch";
import { useSelector } from "@legendapp/state/react";
import { VideoReconnectOverlay } from "./video-reconnect-overlay";
import { Button } from "./button";
import IconX from "~icons/tabler/x";
import { useViewSettings } from "@/view-settings";
import IconLoader2 from "~icons/tabler/loader-2";

import { useHandleDashErrors } from "@/shared/hooks/useHandleDashErrors";
import {
  ControlEvent,
  ControlEventArgs,
  emitter,
} from "@/services/event-emitter";
import { useEffectOnce } from "react-use";
import { LatencyOverlay } from "./latency-overlay";
import { twMerge } from "tailwind-merge";
import { Tooltip } from "@radix-ui/themes";
import { ZoomistWrapper } from "@/components/zoomist-wrapper";
import { Rnd } from "react-rnd";
import { toast } from "react-hot-toast";
import { DASH_PLAYER_SETTINGS } from "@/shared/helpers/dash-player-settings";
import { useCalibration } from "@/virtual-store/hooks/useCalibration";
import { getVideoStreamUrl } from "@/shared/api/helpers";

const VIDEO_START_TIME_KEY = "From-Date-Time";

export function VideoPlayer() {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const viewSettings = useViewSettings();
  const videoPlayback = useVideoPlayback();
  const hasError = useSelector(
    () => videoPlayback.videoContext.error.get()?.type === "error"
  );
  const hasPlaybackError = useSelector(
    () => videoPlayback.videoContext.error.get()?.type === "playbackError"
  );
  const { toggleVideo } = usePlaybackMediator();

  const { attachEventListeners, detachEventListeners } = useHandleDashErrors(
    videoPlayback.dashPlayer
  );
  const mediator = usePlaybackMediator();

  const getVideoMpd = async (start: Date, end: Date | null, isLive = false) => {
    const camera = videoPlayback.videoContext.camera.peek();
    const videoUrl = getVideoStreamUrl({
      cameraId: camera,
      fromDateTime: start,
      toDateTime: end || new Date(),
      isLive,
    });

    try {
      const response = await wretch(videoUrl.href).get().res();
      const videoStartTime = response.headers.get(VIDEO_START_TIME_KEY);

      if (!videoStartTime) {
        toast.error("Cannot read From-Date-Time request header");
        throw Error("Cannot read From-Date-Time request header");
      }

      return {
        // TODO: Remove Z when backend sends ISO
        videoStartTime: videoStartTime,
        videoUrl: videoUrl.href,
      };
    } catch (err) {
      console.error(err);
      videoPlayback.videoContext.error.set(err);

      return {
        videoStartTime: null,
        videoUrl: null,
      };
    }
  };

  const onReconnect = async () => {
    videoPlayback.videoContext.error.set(null);
    mediator.syncRenderingWithVideo();
  };

  const setupDashPlayer = async (params: {
    startDate: Date;
    endDate: Date | null;
    isLive?: boolean;
    seekValue?: number | string;
    autoPlay?: boolean;
  }) => {
    const canPlayVideo =
      viewSettings.showVideo.peek() && !videoPlayback.videoContext.error.peek();

    if (!canPlayVideo)
      return console.warn(
        "Video is not enabled",
        viewSettings.showVideo.peek(),
        videoPlayback.videoContext.error.peek()
      );

    const { videoStartTime, videoUrl } = await getVideoMpd(
      params.startDate,
      params.endDate,
      params.isLive
    );

    if (videoStartTime) {
      if (videoRef.current) {
        videoPlayback.videoContext.startTime.set(new Date(videoStartTime));
        videoPlayback.dashPlayer.initialize(
          videoRef.current,
          videoUrl,
          params.autoPlay || false,
          params.seekValue
        );
        videoPlayback.dashPlayer.updateSettings(DASH_PLAYER_SETTINGS);

        attachEventListeners();
      }
    }
  };

  const destroyDashPlayer = () => {
    detachEventListeners();
    videoPlayback.videoContext.startTime.set(null);
    videoPlayback.videoContext.endTime.set(null);
    videoPlayback.videoContext.duration.set(0);
    videoPlayback.dashPlayer.destroy();
  };

  const onLiveStart = ({ startDate }: ControlEventArgs["LIVE_STARTED"]) => {
    if (!startDate) return;

    setupDashPlayer({ startDate, endDate: null, isLive: true, autoPlay: true });
  };

  const onHistoryStart = ({
    startDate,
    endDate,
  }: ControlEventArgs["HISTORY_STARTED"]) => {
    if (!startDate || !endDate) return;

    setupDashPlayer({ startDate, endDate });
  };

  const onVideoEnabled = ({
    startDate,
    endDate,
    isLive,
    seekValue,
  }: ControlEventArgs["VIDEO_ENABLED"]) => {
    setupDashPlayer({ startDate, endDate, isLive, seekValue });
  };

  const onVideoDisabled = () => {
    destroyDashPlayer();
    console.log("Video disabled");
  };

  useEffectOnce(() => {
    emitter.on(ControlEvent.VideoEnabled, onVideoEnabled);
    emitter.on(ControlEvent.LiveStarted, onLiveStart);
    emitter.on(ControlEvent.HistoryStarted, onHistoryStart);
    emitter.on(ControlEvent.VideoDisabled, onVideoDisabled);

    return () => {
      emitter.off(ControlEvent.VideoEnabled, onVideoEnabled);
      emitter.off(ControlEvent.VideoDisabled, onVideoDisabled);
      emitter.off(ControlEvent.HistoryStarted, onHistoryStart);
      emitter.off(ControlEvent.LiveStarted, onLiveStart);
    };
  });

  return (
    <Rnd
      className="z-[9999]"
      dragHandleClassName="header"
      lockAspectRatio={16 / 9}
      lockAspectRatioExtraHeight={43}
      bounds=".virtual-store"
      minWidth={320}
      minHeight={224}
      default={{
        x: window.innerWidth - 350,
        y: window.innerHeight - 224 - 300,
        width: 320,
        height: 224,
      }}
    >
      <div className="z-[100] bg-black rounded-lg overflow-hidden">
        <header className="header bg-[var(--gray-1)] flex justify-between items-center px-4 p-1 pr-1">
          <CameraLabel />

          <Button className=" p-2" onClick={toggleVideo}>
            <IconX />
          </Button>
        </header>
        <ZoomistWrapper className="relative aspect-video w-full !h-auto">
          {hasPlaybackError && (
            <VideoReconnectOverlay onReconnect={onReconnect} />
          )}
          {hasError && (
            <div className="absolute bg-black flex items-center justify-center aspect-video w-[350px]">
              <p className="text-white mb-2">Video is unavailable</p>
            </div>
          )}
          {!hasError && !hasPlaybackError && <LatencyOverlay />}
          <Spinner />
          <video
            id="player"
            className="w-full h-full object-contain object-center"
            ref={videoRef}
            controls={false}
          />
        </ZoomistWrapper>
      </div>
    </Rnd>
  );
}

export function CameraLabel() {
  const videoPlayback = useVideoPlayback();
  const mediator = usePlaybackMediator();
  const camera = useSelector(() => videoPlayback.videoContext.camera.get());
  const isStandalone = useSelector(() =>
    videoPlayback.videoContext.isStandalone.get()
  );

  const { calibration } = useCalibration();

  return (
    <div className="flex items-center gap-4">
      <span className="text-sm font-medium">
        Camera:{" "}
        <select
          value={camera}
          onChange={(event) => {
            const cameraId = event.target.value;
            mediator.selectCamera(cameraId);
          }}
        >
          {calibration?.camera_calibrations?.map((camera) => (
            <option key={camera.camera_id} value={camera.camera_id}>
              {camera.camera_id}
            </option>
          ))}
        </select>
      </span>
      {isStandalone ? (
        <Tooltip content="Video is played separately from scene animation">
          <span
            onClick={() => videoPlayback.videoContext.isStandalone.toggle()}
            className="border border-amber-300 bg-amber-100 text-amber-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full"
          >
            Standalone
          </span>
        </Tooltip>
      ) : (
        <Tooltip content="Video is played synchronously with scene animation">
          <span
            onClick={() => videoPlayback.videoContext.isStandalone.toggle()}
            className="border border-emerald-300 bg-emerald-100 text-emerald-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full"
          >
            Sync
          </span>
        </Tooltip>
      )}
    </div>
  );
}

export function Spinner() {
  const [isLoading, setIsLoading] = useState(false);
  const videoPlayback = useVideoPlayback();

  const onWaiting = () => {
    setIsLoading(true);
  };

  const onCanPlay = () => {
    setIsLoading(false);
  };

  useEffectOnce(() => {
    videoPlayback.dashPlayer.on("playbackWaiting", onWaiting);
    videoPlayback.dashPlayer.on("canPlay", onCanPlay);

    return () => {
      videoPlayback.dashPlayer.off("onPlaybackWaiting", onWaiting);
      videoPlayback.dashPlayer.off("canPlay", onCanPlay);
    };
  });

  return (
    <div
      className={twMerge([
        `absolute flex z-[50] items-center justify-center pointer-events-none aspect-video w-[350px]`,
        isLoading ? "opacity-1" : "opacity-0",
      ])}
    >
      <div className="text-white text-3xl">
        <IconLoader2 className="animate-spin" />
      </div>
    </div>
  );
}
